// Shattered Metropolis — the street plan, shared by the sim half (hazard placement: downed cables lie across the roads) and
// the render half (the ground shader paints the same roads, kerbs and crossings). PURE: no three, no DOM, no randomness.
//
// A loose grid of boulevards, turned a little off the world axes and gently bent (the city settled after the old
// catastrophe), with a round civic plaza at the spawn where the two central boulevards cross, and one broken diagonal
// avenue. Everything is analytic so the TSL ground and this file agree to the centimetre.

export const CITY = {
  /** grid rotation (rad from +x) */
  angle: 0.28,
  /** block pitch: distance between parallel road centre lines (m) */
  pitch: 38,
  /** road half width (asphalt), and the outer edge of the pavement (m from the road centre line) */
  road: 6.5, walk: 9.5,
  /** bend: amplitude (m) and wave numbers of the gentle sideways drift of the roads */
  bend: 1.4, bendKu: 0.05, bendKv: 0.045,
  /** civic plaza at the spawn (radius, m) */
  plazaR: 15,
  /** diagonal avenue: direction (rad from the grid's u axis), offset of its centre line from the origin (m), half width */
  diagRot: 0.83, diagOff: 21, diagW: 7.5,
} as const;

const C = Math.cos(CITY.angle), S = Math.sin(CITY.angle);

/** world (x, z) → grid (u, v): rotate into the grid frame, then bend. Road centre lines are u = k·pitch and v = k·pitch. */
export function toGrid(x: number, z: number): [number, number] {
  const u0 = x * C + z * S, v0 = -x * S + z * C;
  return [u0 + CITY.bend * Math.sin(v0 * CITY.bendKv + 0.7), v0 + CITY.bend * Math.sin(u0 * CITY.bendKu + 2.1)];
}
/** grid (u, v) → world (x, z) (inverts the bend by fixed-point iteration; error well under a centimetre) */
export function toWorld(u: number, v: number): [number, number] {
  let u0 = u, v0 = v;
  for (let i = 0; i < 6; i++) { u0 = u - CITY.bend * Math.sin(v0 * CITY.bendKv + 0.7); v0 = v - CITY.bend * Math.sin(u0 * CITY.bendKu + 2.1); }
  return [u0 * C - v0 * S, u0 * S + v0 * C];
}
/** world direction (rad from +x) of the grid's u axis (roads running along v have this heading + π/2) */
export const GRID_HEADING = CITY.angle;

/** distance (m) from the nearest road centre line, along either grid axis */
export function roadDist(x: number, z: number): number {
  const [u, v] = toGrid(x, z), P = CITY.pitch;
  const du = Math.abs(u - Math.round(u / P) * P), dv = Math.abs(v - Math.round(v / P) * P);
  return Math.min(du, dv);
}
